import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useNode } from '@splashthat/splash-cms-engine';

import {
    BRAND_LIBRARY_BUTTON_DEFAULT_COLOR,
    BRAND_LIBRARY_DARK_TEXT_COLOR,
    BRAND_LIBRARY_BODY_TEXT,
} from 'common/constants/brandLibrary';
import { useTouchpointData } from 'common/components/TouchpointEditor/contexts/TouchpointDataContext';

import TicketLayout from './TicketLayout';
import {
    ColumnDisplay,
    TicketContainer,
    BoldedItem,
    TicketSaleDataContainer,
    StyledTotal,
} from './TicketConfirmation.styles';

import { exampleTicket } from '../utils/exampleTicket';
import { normalizeTicketData } from '../utils/normalizeTicketData';

const TicketConfirmation = React.forwardRef(
    (
        {
            meta: { visible },
            styles: { fontColor, fontFamily, fontSize },
            descendants: { ticketPrice },
            id,
            hovered,
            selected,
        },
        ref,
    ) => {
        const node = useNode();
        const { event, theme } = useTouchpointData();
        const ticketConfirmation = event?.confirmationsConfig?.tickets || exampleTicket;
        const { shouldHideReceiptTitle, orderNumber, tickets, totalAmount, tax } = useMemo(
            () => normalizeTicketData(ticketConfirmation),
            [ticketConfirmation],
        );

        if (!visible) {
            return null;
        }

        return (
            <TicketContainer
                data-qa={`${id}__modal-wrapper`}
                selected={selected}
                hovered={hovered}
                onMouseLeave={node.actions.onMouseLeave()}
                onMouseMove={node.actions.onMouseMove()}
                onClick={node.actions.onClick()}
                ref={ref}
                $fontColor={theme.colors[fontColor]}
                $fontFamily={theme.activeFonts[fontFamily]}
                $fontSize={fontSize}
            >
                <TicketSaleDataContainer>
                    {!shouldHideReceiptTitle && <BoldedItem as="h3">Your Receipt</BoldedItem>}
                    <h3>
                        <BoldedItem>Order Number:</BoldedItem> #{orderNumber}
                    </h3>
                </TicketSaleDataContainer>
                <ColumnDisplay>
                    {tickets.map((ticket) => (
                        <TicketLayout key={ticket.name} ticket={ticket} />
                    ))}
                </ColumnDisplay>
                {!!tax && <BoldedItem $textAlign="right">Tax: {tax}</BoldedItem>}
                <StyledTotal
                    $textAlign="right"
                    $fontColor={theme.colors[ticketPrice.styles.fontColor]}
                    $fontFamily={theme.activeFonts[ticketPrice.styles.fontFamily]}
                    $fontSize={ticketPrice.styles.fontSize}
                >
                    Total: {totalAmount}
                </StyledTotal>
            </TicketContainer>
        );
    },
);

TicketConfirmation.splash = {
    related: {
        settings: React.lazy(() => import('./TicketConfirmation.tools')),
    },
    defaultProps: {
        meta: {
            visible: true,
            displayName: 'Ticket Confirmation',
        },
        styles: {
            fontSize: 12,
            fontColor: BRAND_LIBRARY_DARK_TEXT_COLOR,
            fontFamily: BRAND_LIBRARY_BODY_TEXT,
        },
        descendants: {
            ticketPrice: {
                styles: {
                    fontSize: 12,
                    fontColor: BRAND_LIBRARY_BUTTON_DEFAULT_COLOR,
                    fontFamily: BRAND_LIBRARY_BODY_TEXT,
                },
            },
        },
    },
};

TicketConfirmation.propTypes = {
    meta: PropTypes.shape({
        visible: PropTypes.bool.isRequired,
    }).isRequired,
    styles: PropTypes.shape({
        fontFamily: PropTypes.number,
        fontSize: PropTypes.number,
        fontColor: PropTypes.number,
    }).isRequired,
    descendants: PropTypes.object.isRequired,
    hovered: PropTypes.bool.isRequired,
    selected: PropTypes.bool.isRequired,
    id: PropTypes.string.isRequired,
};

export default TicketConfirmation;
